import {
  MAX_YOUTH_VALUE,
  DISCORD_NO_VALUE,
  FAVORITE_VALUE,
  MY_CUP_VALUE,
  AVERAGE_TEAM_VALUE,
  BOT_SERIES_VALUE,
  BOT_SERIES_LEVEL_PREFIX,
  SAVED_FILTER_VALUE_PREFIX,
  SERIES_VALUE_PREFIX,
  SPECIALTY_MINUS_VALUE,
  SPECIALTY_PLUS_VALUE,
  URL_TOKEN_MAX_YOUTH,
  URL_TOKEN_DISCORD_NO,
  URL_TOKEN_FAVORITE,
  URL_TOKEN_MY_CUP,
  URL_TOKEN_BOT_SERIES,
  URL_TOKEN_SPECIALTY_MINUS,
  URL_TOKEN_SPECIALTY_PLUS,
} from './constants'

export const readPathToken = () => {
  const path = window.location.pathname || '/'
  const segments = path.split('/').filter(Boolean)

  if (!segments.length) {
    return ''
  }

  return decodeURIComponent(segments[0])
}

export const readMyCupTeamId = () => {
  if ((window.location.pathname || '').replace(/\/+$/, '').toLowerCase() !== '/mycup') {
    return null
  }

  const query = new URLSearchParams(window.location.search)
  const explicitTeamId = query.get('teamId') || query.get('id')
  const bareTeamId = explicitTeamId || Array.from(query.keys())[0]
  const teamId = Number.parseInt(bareTeamId, 10)

  return Number.isInteger(teamId) && teamId > 0 ? teamId : null
}

const extractSeriesPrefix = value => {
  const normalized = `${value || ''}`.trim().toUpperCase()

  if (!normalized || normalized.startsWith('SERIES-')) {
    return null
  }

  const dotMatch = normalized.match(/^([A-Z0-9]+)\.(\d+)$/)
  if (dotMatch) {
    return dotMatch[1]
  }

  const strictMatch = normalized.match(/^([A-Z0-9]+)$/)
  if (strictMatch) {
    return strictMatch[1]
  }

  return null
}

const buildCanonicalSeriesToken = ({ seriesName, seriesId }) => {
  const normalizedName = `${seriesName || ''}`.trim()
  const normalizedSeriesId = Number.parseInt(seriesId, 10)
  const dottedMatch = normalizedName.match(/^([A-Za-z0-9]+)\.(\d+)$/)

  if (dottedMatch) {
    const displayNumber = Number.parseInt(dottedMatch[2], 10)

    if (Number.isInteger(displayNumber) && displayNumber > 0) {
      return `${dottedMatch[1].toUpperCase()}.${displayNumber}`
    }
  }

  if (!Number.isInteger(normalizedSeriesId) || normalizedSeriesId <= 0) {
    return ''
  }

  const prefix = extractSeriesPrefix(normalizedName)
  if (prefix) {
    return `${prefix}.${normalizedSeriesId}`
  }

  if (/^SERIES-\d+$/i.test(normalizedName)) {
    return `SERIES-${normalizedSeriesId}`
  }

  return ''
}

const resolveSeriesSelectionFromToken = (token, currentUser) => {
  const normalized = `${token || ''}`.trim()

  if (!normalized) {
    return null
  }

  // Try to extract series ID from explicit series formats like "VII.1443" or "SERIES-1443"
  let seriesId = null
  
  // First try to match "SERIES-" prefix
  if (normalized.toUpperCase().startsWith('SERIES-')) {
    seriesId = Number.parseInt(normalized.slice(7), 10)
  } else {
    // Try to match format like "VII.1443" or "I.100"
    const dotMatch = normalized.match(/\.(\d+)$/)
    if (dotMatch) {
      seriesId = Number.parseInt(dotMatch[1], 10)
    }
  }

  if (Number.isInteger(seriesId) && seriesId > 0) {
    return `${SERIES_VALUE_PREFIX}${seriesId}`
  }

  const configuredSeriesId = Number.parseInt(currentUser?.femmeSeries?.seriesId, 10)
  const configuredSeriesName = `${currentUser?.femmeSeries?.seriesName || ''}`.trim()

  if (
    configuredSeriesName &&
    configuredSeriesId > 0 &&
    configuredSeriesName.toLowerCase() === normalized.toLowerCase()
  ) {
    return `${SERIES_VALUE_PREFIX}${configuredSeriesId}`
  }

  return null
}

const resolveBotSeriesSelectionFromToken = token => {
  const normalized = `${token || ''}`.trim().toUpperCase()

  if (normalized === URL_TOKEN_BOT_SERIES || normalized === 'BOT-SERIES') {
    return `${BOT_SERIES_LEVEL_PREFIX}VI`
  }

  const levelTokenMatch = normalized.match(/^BOTSERIES-(VI|VII)$/)
  if (levelTokenMatch) {
    return `${BOT_SERIES_LEVEL_PREFIX}${levelTokenMatch[1]}`
  }

  return null
}

export const resolveSelectionFromPathToken = ({ pathToken, countries, currentUser }) => {
  const token = `${pathToken || ''}`.trim()

  if (!token) {
    return ''
  }

  const upperToken = token.toUpperCase()

  if (upperToken === URL_TOKEN_DISCORD_NO) {
    return DISCORD_NO_VALUE
  }

  // 'FILTER' token maps to the special filter selection
  if (upperToken === 'FILTER') {
    return 'FILTER'
  }

  if (upperToken === URL_TOKEN_MAX_YOUTH) {
    return MAX_YOUTH_VALUE
  }

  if (upperToken === URL_TOKEN_FAVORITE) {
    return FAVORITE_VALUE
  }

  if (upperToken === URL_TOKEN_MY_CUP.toUpperCase()) {
    return MY_CUP_VALUE
  }

  if (upperToken === 'AVERAGE') {
    return AVERAGE_TEAM_VALUE
  }

  const botSeriesSelection = resolveBotSeriesSelectionFromToken(token)
  if (botSeriesSelection) {
    return botSeriesSelection
  }

  if (upperToken === URL_TOKEN_SPECIALTY_PLUS) {
    return SPECIALTY_PLUS_VALUE
  }

  if (upperToken === URL_TOKEN_SPECIALTY_MINUS) {
    return SPECIALTY_MINUS_VALUE
  }

  if (upperToken.startsWith('FILTER-')) {
    const savedFilterId = token.slice('FILTER-'.length)
    return savedFilterId ? `${SAVED_FILTER_VALUE_PREFIX}${savedFilterId}` : ''
  }

  const countryById = countries.find(country => `${country?.CountryID || ''}` === token)
  if (countryById?.CountryID) {
    return `${countryById.CountryID}`
  }

  const countryByCode = countries.find(
    country => `${country?.CountryCode || ''}`.toLowerCase() === token.toLowerCase()
  )

  if (countryByCode?.CountryID) {
    return `${countryByCode.CountryID}`
  }

  const seriesSelection = resolveSeriesSelectionFromToken(token, currentUser)
  if (seriesSelection) {
    return seriesSelection
  }

  return null
}

export const resolveInitialSelectionFromPathToken = pathToken => {
  const token = `${pathToken || ''}`.trim()

  if (!token) {
    return ''
  }

  const upperToken = token.toUpperCase()

  if (upperToken === URL_TOKEN_DISCORD_NO) {
    return DISCORD_NO_VALUE
  }

  if (upperToken === URL_TOKEN_MAX_YOUTH) {
    return MAX_YOUTH_VALUE
  }

  if (upperToken === URL_TOKEN_FAVORITE) {
    return FAVORITE_VALUE
  }

  if (upperToken === URL_TOKEN_MY_CUP.toUpperCase()) {
    return MY_CUP_VALUE
  }

  if (upperToken === 'AVERAGE') {
    return AVERAGE_TEAM_VALUE
  }

  const botSeriesSelection = resolveBotSeriesSelectionFromToken(token)
  if (botSeriesSelection) {
    return botSeriesSelection
  }

  if (upperToken === URL_TOKEN_SPECIALTY_PLUS) {
    return SPECIALTY_PLUS_VALUE
  }

  if (upperToken === URL_TOKEN_SPECIALTY_MINUS) {
    return SPECIALTY_MINUS_VALUE
  }

  if (upperToken.startsWith('FILTER-')) {
    const savedFilterId = token.slice('FILTER-'.length)
    return savedFilterId ? `${SAVED_FILTER_VALUE_PREFIX}${savedFilterId}` : ''
  }

  // Resolve initial series selection only from explicit series token formats.
  // Numeric path tokens are reserved for country IDs until countries are loaded.
  let seriesId = null
  
  if (upperToken.startsWith('SERIES-')) {
    seriesId = Number.parseInt(token.slice(7), 10)
  } else {
    const dotMatch = token.match(/\.(\d+)$/)
    if (dotMatch) {
      seriesId = Number.parseInt(dotMatch[1], 10)
    }
  }

  if (Number.isInteger(seriesId) && seriesId > 0) {
    return `${SERIES_VALUE_PREFIX}${seriesId}`
  }

  // Support initial load for /FILTER path
  if (upperToken === 'FILTER') {
    return 'FILTER'
  }

  return ''
}

export const toPathTokenFromSelection = ({ selectedCountryCode, countries, currentUser, pathToken }) => {

  if (!selectedCountryCode) {
    return ''
  }

  if (selectedCountryCode === DISCORD_NO_VALUE) {
    return URL_TOKEN_DISCORD_NO
  }

  if (selectedCountryCode === MAX_YOUTH_VALUE) {
    return URL_TOKEN_MAX_YOUTH
  }

  if (selectedCountryCode === FAVORITE_VALUE) {
    return URL_TOKEN_FAVORITE
  }

  if (selectedCountryCode === MY_CUP_VALUE) {
    return URL_TOKEN_MY_CUP
  }

  if (selectedCountryCode === AVERAGE_TEAM_VALUE) {
    return 'AVERAGE'
  }

  if (selectedCountryCode === BOT_SERIES_VALUE) {
    return `${URL_TOKEN_BOT_SERIES}-VI`
  }

  if (selectedCountryCode.startsWith(BOT_SERIES_LEVEL_PREFIX)) {
    return `BOTSERIES-${selectedCountryCode.slice(BOT_SERIES_LEVEL_PREFIX.length).toUpperCase()}`
  }

  if (selectedCountryCode === SPECIALTY_PLUS_VALUE) {
    return URL_TOKEN_SPECIALTY_PLUS
  }

  if (selectedCountryCode === SPECIALTY_MINUS_VALUE) {
    return URL_TOKEN_SPECIALTY_MINUS
  }

  if (selectedCountryCode.startsWith(SAVED_FILTER_VALUE_PREFIX)) {
    const savedFilterId = selectedCountryCode.slice(SAVED_FILTER_VALUE_PREFIX.length)
    return savedFilterId ? `FILTER-${savedFilterId}` : ''
  }

  if (selectedCountryCode.startsWith(SERIES_VALUE_PREFIX)) {
    const selectedSeriesId = Number.parseInt(
      selectedCountryCode.slice(SERIES_VALUE_PREFIX.length),
      10
    )
    const currentSeriesId = Number.parseInt(currentUser?.femmeSeries?.seriesId, 10)
    const currentSeriesToken = buildCanonicalSeriesToken({
      seriesName: currentUser?.femmeSeries?.seriesName,
      seriesId: currentSeriesId,
    })
    const currentSeriesDisplayMatch = `${currentSeriesToken || ''}`
      .trim()
      .match(/^(?:[A-Za-z0-9]+)\.(\d+)$/)
    const currentSeriesDisplayNumber = currentSeriesDisplayMatch
      ? Number.parseInt(currentSeriesDisplayMatch[1], 10)
      : null

    if (
      Number.isInteger(selectedSeriesId) &&
      selectedSeriesId > 0 &&
      (
        selectedSeriesId === currentSeriesId
        || (Number.isInteger(currentSeriesDisplayNumber) && selectedSeriesId === currentSeriesDisplayNumber)
      ) &&
      currentSeriesToken
    ) {
      return currentSeriesToken
    }

    // Check if pathToken contains the same series ID
    if (pathToken) {
      const trimmedPathToken = `${pathToken || ''}`.trim()
      const pathTokenMatch = trimmedPathToken.match(/\.(\d+)$/)
      if (pathTokenMatch) {
        const pathTokenSeriesId = Number.parseInt(pathTokenMatch[1], 10)
        if (Number.isInteger(pathTokenSeriesId) && pathTokenSeriesId === selectedSeriesId) {
          return trimmedPathToken
        }
      }

      const prefixedSeriesMatch = trimmedPathToken.match(/^SERIES-(\d+)$/i)
      if (prefixedSeriesMatch) {
        const pathTokenSeriesId = Number.parseInt(prefixedSeriesMatch[1], 10)
        if (Number.isInteger(pathTokenSeriesId) && pathTokenSeriesId === selectedSeriesId) {
          return `SERIES-${pathTokenSeriesId}`
        }
      }
    }

    if (Number.isInteger(selectedSeriesId) && selectedSeriesId > 0) {
      const result = `SERIES-${selectedSeriesId}`
      return result
    }

    return ''
  }

  const selectedCountry = countries.find(country => `${country?.CountryID || ''}` === `${selectedCountryCode}`)

  if (selectedCountry?.CountryID) {
    return `${selectedCountry.CountryID}`
  }

  return selectedCountryCode
}

const MATCH_DATE_TIME_ZONE = 'Europe/Stockholm'
const MATCH_DURATION_MS = 2.5 * 60 * 60 * 1000
const ADD_LIVE_LOOKAHEAD_MS = 4 * 24 * 60 * 60 * 1000
const MATCH_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/

const stockholmDateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: MATCH_DATE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

const toUpcomingMatchesArray = upcomingMatches => {
  if (Array.isArray(upcomingMatches)) {
    return upcomingMatches
  }

  if (upcomingMatches && typeof upcomingMatches === 'object') {
    return Object.values(upcomingMatches)
  }

  return []
}

const toMatchId = match => {
  const parsed = Number.parseInt(match?.MatchID, 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

const toMatchType = match => {
  const parsed = Number.parseInt(match?.MatchTypeID ?? match?.MatchType, 10)
  return Number.isInteger(parsed) ? parsed : null
}

const toDateTimePartsInTimeZone = date => {
  const parts = stockholmDateTimeFormatter.formatToParts(date)

  return parts.reduce((accumulator, part) => {
    if (part.type !== 'literal') {
      accumulator[part.type] = Number.parseInt(part.value, 10)
    }

    return accumulator
  }, {})
}

const toUtcMillisFromStockholmDateTime = ({ year, month, day, hour, minute, second }) => {
  const targetMillis = Date.UTC(year, month - 1, day, hour, minute, second)
  let utcMillis = targetMillis

  for (let iteration = 0; iteration < 3; iteration += 1) {
    const formattedParts = toDateTimePartsInTimeZone(new Date(utcMillis))
    const formattedMillis = Date.UTC(
      formattedParts.year,
      formattedParts.month - 1,
      formattedParts.day,
      formattedParts.hour,
      formattedParts.minute,
      formattedParts.second
    )
    const offsetMillis = formattedMillis - utcMillis
    const nextUtcMillis = targetMillis - offsetMillis

    if (nextUtcMillis === utcMillis) {
      break
    }

    utcMillis = nextUtcMillis
  }

  return utcMillis
}

const parseStockholmMatchDate = matchDate => {
  const normalized = `${matchDate || ''}`.trim()
  const match = normalized.match(MATCH_DATE_PATTERN)

  if (!match) {
    return null
  }

  const year = Number.parseInt(match[1], 10)
  const month = Number.parseInt(match[2], 10)
  const day = Number.parseInt(match[3], 10)
  const hour = Number.parseInt(match[4], 10)
  const minute = Number.parseInt(match[5], 10)
  const second = Number.parseInt(match[6], 10)

  if ([year, month, day, hour, minute, second].some(value => !Number.isInteger(value))) {
    return null
  }

  return toUtcMillisFromStockholmDateTime({ year, month, day, hour, minute, second })
}

const getAllowedMatchTypesForAddLive = now => {
  const stockholmParts = toDateTimePartsInTimeZone(now)
  const stockholmWeekday = Date.UTC(
    stockholmParts.year,
    stockholmParts.month - 1,
    stockholmParts.day
  )
  const day = new Date(stockholmWeekday).getUTCDay()

  if (day >= 0 && day <= 2) {
    return new Set([3])
  }

  return new Set([1, 2])
}

export const selectAddLiveMatch = upcomingMatches => {
  const now = Date.now()
  const nowDate = new Date(now)
  const latestAllowedStart = now + ADD_LIVE_LOOKAHEAD_MS
  const allowedMatchTypes = getAllowedMatchTypesForAddLive(nowDate)

  const parsedMatches = toUpcomingMatchesArray(upcomingMatches)
    .map(match => ({
      match,
      matchType: toMatchType(match),
      startTime: parseStockholmMatchDate(match?.MatchDate),
    }))
    .filter(item => Number.isFinite(item.startTime))
    .sort((first, second) => {
      const timeDiff = first.startTime - second.startTime

      if (timeDiff !== 0) {
        return timeDiff
      }

      const firstMatchId = toMatchId(first.match) || 0
      const secondMatchId = toMatchId(second.match) || 0

      return firstMatchId - secondMatchId
    })

  return parsedMatches.find(item => {
    if (!allowedMatchTypes.has(item.matchType)) {
      return false
    }

    if (item.startTime > latestAllowedStart) {
      return false
    }

    return item.startTime + MATCH_DURATION_MS > now
  })?.match || null
}

export const buildTeamsCacheKey = ({
  favorite,
  filteredCountryId,
  specialtyMode,
  discordNo,
  maxYouth,
  seriesId,
  authScope,
  countryScope,
  useServerSort,
  sortBy,
  sortDirection,
  playerStatsScope,
}) => {
  return [
    `favorite=${favorite ? '1' : '0'}`,
    `country=${Number.isInteger(filteredCountryId) ? filteredCountryId : ''}`,
    `countryScope=${countryScope === 'team' ? 'team' : 'user'}`,
    `specialty=${specialtyMode || ''}`,
    `discordNo=${discordNo ? '1' : '0'}`,
    `maxYouth=${maxYouth ? '1' : '0'}`,
    `series=${Number.isInteger(seriesId) ? seriesId : ''}`,
    `auth=${authScope}`,
    `serverSort=${useServerSort ? '1' : '0'}`,
    `sortBy=${useServerSort ? sortBy || '' : ''}`,
    `sortDirection=${useServerSort ? sortDirection || '' : ''}`,
    `playerStatsScope=${useServerSort ? playerStatsScope || '' : ''}`,
  ].join('|')
}

export const getStoredOAuthToken = () => {
  const params = new URLSearchParams(window.location.search)
  const callbackToken = params.get('oauth_token')

  if (callbackToken) {
    localStorage.setItem('oauth_token', callbackToken)
    const cleanUrl = `${window.location.origin}${window.location.pathname}`
    window.history.replaceState({}, document.title, cleanUrl)
    return callbackToken
  }

  return localStorage.getItem('oauth_token')
}
